import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert, Box, Button, Checkbox, Chip, FormControlLabel, IconButton, MenuItem, Paper, Select, Stack, Switch, Table, TableBody,
    TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useCan } from '../auth/Can';
import { AutomationRow, getSupportSettings, listAutomations, saveSupportSetting, SupportSetting } from '../services/supportService';
import { RESOLUTION_CODES, STATUS_LABELS } from '../constants/support';

/**
 * R2b-4 (owner, 21 Oct): Customer Support → Support settings. The support App Config keys under one roof, each with a
 * form, validated again on the server. Leads edit the desk settings; SOS, numbers and switches need config:write.
 */
const GROUPS = ['Desk', 'Replies', 'SLA', 'Automations', 'SOS & contact'];
const QUEUES: Record<string, string> = { rider: 'Rider', driver: 'Driver', payments: 'Payments' };
const LEVELS = ['P0', 'P1', 'P2', 'P3'];
const MACRO_STATUSES = ['open', 'in_progress', 'pending_customer', 'resolved']; // no On hold: it needs a typed reason
const NUMBER_KEYS = ['SUPPORT_COMPENSATION_CAP', 'SUPPORT_MAX_REOPEN', 'SUPPORT_PENDING_REMINDER_HOURS', 'SUPPORT_PENDING_AUTORESOLVE_HOURS', 'SUPPORT_RESOLVED_AUTOCLOSE_DAYS'];

const clone = (v: any) => JSON.parse(JSON.stringify(v ?? null));
const rows = (v: any): any[] => (Array.isArray(v) ? v : []).filter((r) => r && typeof r === 'object' && !Array.isArray(r));
/** The form's working copy of a stored value (a hand-edited row never crashes the page). */
const toDraft = (key: string, v: any): any => {
    if (key === 'SUPPORT_ONCALL_NUMBERS') return (Array.isArray(v) ? v : []).filter((n) => typeof n === 'string' || typeof n === 'number').join('\n');
    if (key === 'SUPPORT_MACROS') return rows(v).map((m: any) => ({ ...m, tags: Array.isArray(m.tags) ? m.tags.join(', ') : typeof m.tags === 'string' ? m.tags : '' }));
    if (key === 'SUPPORT_CANNED_REPLIES') return clone(rows(v));
    return clone(v);
};
/** What is sent to the server (it validates again). */
const fromDraft = (key: string, d: any): any => {
    if (key === 'SUPPORT_ONCALL_NUMBERS') return String(d).split(/[\n,;]+/).map((s) => s.trim()).filter(Boolean);
    if (key === 'SUPPORT_MACROS') return d.map((m: any) => ({ ...m, tags: String(m.tags ?? '').split(',').map((t: string) => t.trim()).filter(Boolean) }));
    if (NUMBER_KEYS.includes(key) || key === 'SUPPORT_SOS_LADDER') return d;
    return d;
};
const num = (s: string) => (s === '' ? '' : Number(s));

type EditorProps = { draft: any; set: (d: any) => void; disabled: boolean; automations: AutomationRow[] };

const NewTicketPush: React.FC<EditorProps> = ({ draft, set, disabled }) => (
    <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
        <Select size="small" value={draft.mode} disabled={disabled} onChange={(e) => set({ ...draft, mode: e.target.value })} inputProps={{ 'aria-label': 'mode' }}>
            <MenuItem value="each">One push per ticket</MenuItem>
            <MenuItem value="summary">A summary every few minutes</MenuItem>
            <MenuItem value="off">Off (P0 safety still rings)</MenuItem>
        </Select>
        {draft.mode === 'summary' && (
            <TextField size="small" type="number" label="Minutes" value={draft.minutes} disabled={disabled} onChange={(e) => set({ ...draft, minutes: num(e.target.value) })} sx={{ width: 120 }} />
        )}
    </Stack>
);

const AutoAssign: React.FC<EditorProps> = ({ draft, set, disabled }) => (
    <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
        {Object.entries(QUEUES).map(([q, label]) => (
            <FormControlLabel key={q} label={label} disabled={disabled} control={
                <Checkbox checked={draft.queues.includes(q)} onChange={(e) => set({ ...draft, queues: e.target.checked ? [...draft.queues, q] : draft.queues.filter((x: string) => x !== q) })} />
            } />
        ))}
        <TextField size="small" type="number" label="Same agent for repeat contacts (days)" value={draft.repeatContactDays} disabled={disabled} onChange={(e) => set({ ...draft, repeatContactDays: num(e.target.value) })} sx={{ width: 260 }} />
    </Stack>
);

const NumberField: React.FC<EditorProps> = ({ draft, set, disabled }) => (
    <TextField size="small" type="number" value={draft ?? ''} disabled={disabled} onChange={(e) => set(num(e.target.value))} sx={{ width: 160 }} inputProps={{ 'aria-label': 'value' }} />
);

const RowsEditor: React.FC<EditorProps & { fields: Array<{ k: string; label: string; wide?: boolean; options?: Record<string, string> }>; blank: any }> = ({ draft, set, disabled, fields, blank }) => (
    <Box>
        {draft.map((row: any, i: number) => (
            <Stack key={i} direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
                {fields.map((f) => f.options ? (
                    <Select key={f.k} size="small" displayEmpty value={row[f.k] ?? ''} disabled={disabled} onChange={(e) => set(draft.map((r: any, j: number) => (j === i ? { ...r, [f.k]: e.target.value } : r)))} sx={{ minWidth: 150 }} inputProps={{ 'aria-label': f.label }}>
                        <MenuItem value="">{f.label}: none</MenuItem>
                        {Object.entries(f.options).map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
                    </Select>
                ) : (
                    <TextField key={f.k} size="small" label={f.label} value={row[f.k] ?? ''} disabled={disabled} multiline={f.wide} minRows={f.wide ? 2 : undefined}
                        onChange={(e) => set(draft.map((r: any, j: number) => (j === i ? { ...r, [f.k]: e.target.value } : r)))} sx={{ minWidth: f.wide ? 320 : 140, flex: f.wide ? 1 : undefined }} />
                ))}
                <IconButton aria-label="Remove" disabled={disabled} onClick={() => set(draft.filter((_: any, j: number) => j !== i))}><DeleteOutlineIcon fontSize="small" /></IconButton>
            </Stack>
        ))}
        <Button size="small" disabled={disabled} onClick={() => set([...draft, clone(blank)])}>Add</Button>
    </Box>
);

const SlaEditor: React.FC<EditorProps> = ({ draft, set, disabled }) => {
    const setLevel = (L: string, f: string, v: string) => set({ ...draft, levels: { ...draft.levels, [L]: { ...draft.levels[L], [f]: num(v) } } });
    const setHour = (L: string, f: string, v: string) => set({ ...draft, hours: { ...draft.hours, [L]: { ...(draft.hours[L] ?? { start: 9, end: 21 }), [f]: num(v) } } });
    return (
        <Box>
            <Table size="small" sx={{ maxWidth: 640 }}>
                <TableHead><TableRow><TableCell>Level</TableCell><TableCell>First reply (min)</TableCell><TableCell>Next reply (min)</TableCell><TableCell>Resolve (min)</TableCell></TableRow></TableHead>
                <TableBody>
                    {LEVELS.map((L) => (
                        <TableRow key={L}>
                            <TableCell>{L}</TableCell>
                            {['first', 'next', 'resolve'].map((f) => (
                                <TableCell key={f}><TextField size="small" type="number" value={draft.levels[L][f]} disabled={disabled} onChange={(e) => setLevel(L, f, e.target.value)} inputProps={{ 'aria-label': `${L} ${f}` }} sx={{ width: 110 }} /></TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            <FormControlLabel sx={{ mt: 1 }} disabled={disabled} label="Business hours for P2 / P3 (IST)" control={<Switch checked={!!draft.businessHours} onChange={(e) => set({ ...draft, businessHours: e.target.checked })} />} />
            <Stack direction="row" spacing={2} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                {['P2', 'P3'].map((L) => (
                    <React.Fragment key={L}>
                        <TextField size="small" type="number" label={`${L} from (hour)`} value={draft.hours[L]?.start ?? ''} disabled={disabled} onChange={(e) => setHour(L, 'start', e.target.value)} sx={{ width: 130 }} />
                        <TextField size="small" type="number" label={`${L} to (hour)`} value={draft.hours[L]?.end ?? ''} disabled={disabled} onChange={(e) => setHour(L, 'end', e.target.value)} sx={{ width: 130 }} />
                    </React.Fragment>
                ))}
                <TextField size="small" type="number" label="Misconduct inquiry (days)" value={draft.inquiryDays} disabled={disabled} onChange={(e) => set({ ...draft, inquiryDays: num(e.target.value) })} sx={{ width: 200 }} />
                <TextField size="small" type="number" label="Suspend decision (min)" value={draft.suspendDecisionMinutes} disabled={disabled} onChange={(e) => set({ ...draft, suspendDecisionMinutes: num(e.target.value) })} sx={{ width: 200 }} />
            </Stack>
        </Box>
    );
};

const AutomationsEditor: React.FC<EditorProps> = ({ draft, set, disabled, automations }) => (
    <Box>
        {automations.filter((a) => a.switchable).map((a) => (
            <FormControlLabel key={a.key} sx={{ display: 'flex' }} disabled={disabled} label={a.label} control={
                <Switch checked={draft[a.key] ?? a.enabled} onChange={(e) => set({ ...draft, [a.key]: e.target.checked })} inputProps={{ 'aria-label': a.key }} />
            } />
        ))}
        {automations.filter((a) => !a.switchable).map((a) => (
            <Typography key={a.key} variant="body2" color="text.secondary" sx={{ ml: 1 }}>• {a.label} <em>(always on)</em></Typography>
        ))}
    </Box>
);

const SosSms: React.FC<EditorProps> = ({ draft, set, disabled }) => (
    <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
        <FormControlLabel disabled={disabled} label={draft.enabled ? 'On' : 'Off'} control={<Switch checked={!!draft.enabled} onChange={(e) => set({ ...draft, enabled: e.target.checked })} inputProps={{ 'aria-label': 'SOS SMS on' }} />} />
        <TextField size="small" label="Trusted-contact flow id" value={draft.contactFlowId ?? ''} disabled={disabled} onChange={(e) => set({ ...draft, contactFlowId: e.target.value })} />
        <TextField size="small" label="On-call flow id" value={draft.onCallFlowId ?? ''} disabled={disabled} onChange={(e) => set({ ...draft, onCallFlowId: e.target.value })} />
    </Stack>
);

const Ladder: React.FC<EditorProps> = ({ draft, set, disabled }) => (
    <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
        <TextField size="small" type="number" label="All responders after (min)" value={draft.allResponders} disabled={disabled} onChange={(e) => set({ ...draft, allResponders: num(e.target.value) })} sx={{ width: 210 }} />
        <TextField size="small" type="number" label="On-call SMS after (min)" value={draft.sms} disabled={disabled} onChange={(e) => set({ ...draft, sms: num(e.target.value) })} sx={{ width: 200 }} />
        <TextField size="small" type="number" label="Repeat every (min)" value={draft.repeat} disabled={disabled} onChange={(e) => set({ ...draft, repeat: num(e.target.value) })} sx={{ width: 180 }} />
    </Stack>
);

const Grievance: React.FC<EditorProps> = ({ draft, set, disabled }) => (
    <Stack spacing={1} sx={{ maxWidth: 640 }}>
        <FormControlLabel disabled={disabled} label="Published (customers see it after a resolved ticket)" control={<Switch checked={!!draft.published} onChange={(e) => set({ ...draft, published: e.target.checked })} />} />
        {[['name', 'Name'], ['designation', 'Designation'], ['email', 'Email'], ['phone', 'Phone'], ['address', 'Address'], ['hours', 'Hours']].map(([k, l]) => (
            <TextField key={k} size="small" label={l} value={draft[k] ?? ''} disabled={disabled} onChange={(e) => set({ ...draft, [k]: e.target.value })} />
        ))}
    </Stack>
);

const editorFor = (key: string): React.FC<EditorProps> => {
    switch (key) {
        case 'SUPPORT_NEW_TICKET_PUSH': return NewTicketPush;
        case 'SUPPORT_AUTO_ASSIGN': return AutoAssign;
        case 'SUPPORT_CANNED_REPLIES': return (p) => <RowsEditor {...p} blank={{ label: '', text: '', hi: '' }} fields={[{ k: 'label', label: 'Label' }, { k: 'text', label: 'Reply', wide: true }, { k: 'hi', label: 'Hindi (optional)', wide: true }]} />;
        case 'SUPPORT_MACROS': return (p) => (
            <RowsEditor {...p} blank={{ id: '', label: '', text: '', hi: '', status: '', resolutionCode: '', tags: '' }} fields={[
                { k: 'id', label: 'Id (a–z, _)' }, { k: 'label', label: 'Label' }, { k: 'text', label: 'Reply', wide: true }, { k: 'hi', label: 'Hindi (optional)', wide: true },
                { k: 'status', label: 'Status', options: Object.fromEntries(MACRO_STATUSES.map((s) => [s, STATUS_LABELS[s] ?? s])) },
                { k: 'resolutionCode', label: 'Resolution', options: RESOLUTION_CODES }, { k: 'tags', label: 'Tags (comma)' }, // staff codes only (no sos_* / auto)
            ]} />
        );
        case 'SUPPORT_SLA_POLICY': return SlaEditor;
        case 'SUPPORT_AUTOMATIONS': return AutomationsEditor;
        case 'SOS_SMS': return SosSms;
        case 'SUPPORT_ONCALL_NUMBERS': return ({ draft, set, disabled }) => (
            <TextField size="small" multiline minRows={2} label="One number per line" value={draft} disabled={disabled} onChange={(e) => set(e.target.value)} sx={{ width: 320 }} />
        );
        case 'SUPPORT_SOS_LADDER': return Ladder;
        case 'SUPPORT_PHONE': return ({ draft, set, disabled }) => (
            <TextField size="small" label="Phone (empty hides Call support)" value={draft ?? ''} disabled={disabled} onChange={(e) => set(e.target.value)} sx={{ width: 320 }} />
        );
        case 'SUPPORT_GRIEVANCE_OFFICER': return Grievance;
        case 'CHAT_BOT_ENABLED': return ({ draft, set, disabled }) => (
            <FormControlLabel disabled={disabled} label={draft ? 'On' : 'Off'} control={<Switch checked={!!draft} onChange={(e) => set(e.target.checked)} inputProps={{ 'aria-label': 'FAQ bot on' }} />} />
        );
        default: return NumberField;
    }
};

const SettingCard: React.FC<{ s: SupportSetting; isAdmin: boolean; automations: AutomationRow[]; onSaved: (key: string, value: any) => void }> = ({ s, isAdmin, automations, onSaved }) => {
    const [draft, setDraft] = useState<any>(() => toDraft(s.key, s.value));
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
    useEffect(() => { setDraft(toDraft(s.key, s.value)); }, [s.key, s.value]);
    const locked = s.editableBy === 'admin' && !isAdmin;
    const Editor = useMemo(() => editorFor(s.key), [s.key]);
    const save = async () => {
        setSaving(true);
        try {
            const r = await saveSupportSetting(s.key, fromDraft(s.key, draft), s.value);
            onSaved(s.key, r.data.value);
            setMsg({ ok: true, text: `Saved — applies within ${s.applies}.` });
        } catch (e: any) {
            setMsg({ ok: false, text: e?.response?.status === 413 ? 'Too big to save — shorten the texts or remove some rows.' : e?.response?.data?.message || 'Could not save' });
        } finally {
            setSaving(false);
        }
    };
    return (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }} data-testid={`setting-${s.key}`}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }} flexWrap="wrap" useFlexGap>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{s.label}</Typography>
                <Chip size="small" variant="outlined" label={`applies within ${s.applies}`} />
                {s.isDefault && <Chip size="small" variant="outlined" label="default" />}
                {s.editableBy === 'admin' && <Chip size="small" color={locked ? 'default' : 'warning'} variant="outlined" label="admin only" />}
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{s.help}</Typography>
            <Editor draft={draft} set={setDraft} disabled={locked || saving} automations={automations} />
            {!locked && (
                <Box sx={{ mt: 1.5 }}>
                    <Button size="small" variant="contained" disabled={saving} onClick={save}>Save</Button>
                </Box>
            )}
            {msg && <Alert severity={msg.ok ? 'success' : 'error'} sx={{ mt: 1 }} onClose={() => setMsg(null)}>{msg.text}</Alert>}
        </Paper>
    );
};

const SupportSettings: React.FC = () => {
    const { can } = useCan();
    const isAdmin = can('config:write');
    const [settings, setSettings] = useState<SupportSetting[] | null>(null);
    const [automations, setAutomations] = useState<AutomationRow[]>([]);
    const [error, setError] = useState<string | null>(null);
    const load = useCallback(() => {
        getSupportSettings().then((r) => setSettings(r.data.settings)).catch((e) => setError(e?.response?.data?.message || 'Could not load the settings'));
        listAutomations().then((r) => setAutomations(r.data.automations)).catch(() => setAutomations([]));
    }, []);
    useEffect(() => { load(); }, [load]);
    const onSaved = (key: string, value: any) => setSettings((prev) => prev && prev.map((s) => (s.key === key ? { ...s, value, isDefault: false } : s)));

    return (
        <Box sx={{ p: 2, maxWidth: 1200, mx: 'auto' }}>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>Support settings</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Every customer-support setting in one place. Each is checked again on the server before it is saved, and every change is in the audit log.
                {!isAdmin && ' Settings marked "admin only" need the config:write tick.'}
            </Typography>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {settings && GROUPS.map((g) => (
                <Box key={g} sx={{ mb: 3 }} data-testid={`group-${g}`}>
                    <Typography variant="h6" sx={{ mb: 1 }}>{g}</Typography>
                    {settings.filter((s) => s.group === g).map((s) => <SettingCard key={s.key} s={s} isAdmin={isAdmin} automations={automations} onSaved={onSaved} />)}
                </Box>
            ))}
        </Box>
    );
};

export default SupportSettings;
